/**
 * plataforma.js — o manual da plataforma web, vertical Estacionamento Comercial
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 92)
 *
 * POR QUE O CONTEÚDO É DADO, E NÃO JSX
 *
 * Um manual escrito direto em JSX vira um arquivo de mil linhas onde texto e
 * marcação se confundem, e onde ninguém consegue responder "o que o operador
 * vê?" sem ler tudo. Aqui cada seção declara a que PERFIS pertence, e a tela
 * filtra. A mesma fonte serve ao gestor e ao operador sem duplicar uma frase.
 *
 * PERFIS
 *
 * A plataforma tem quatro (`superadmin`, `admin`, `operator`, `viewer`), mas o
 * manual trata de dois públicos, que é como as pessoas se enxergam:
 *
 *   'gestor'   — quem responde pelo estacionamento: tarifa, usuários,
 *                fechamento, estorno, isenção. Cobre `admin` e `superadmin`.
 *   'operador' — quem está no turno: caixa, tickets, pátio, leitura de placa.
 *                Cobre `operator`, e o `viewer` lê sem poder agir.
 *
 * Uma seção sem `perfis` vale para os dois.
 *
 * A INSTALAÇÃO (Onda 154)
 *
 * O manual nasceu para o Estacionamento Comercial e serve hoje às três verticais. Seções, blocos e
 * linhas de tabela podem declarar a que instalação pertencem — a tela filtra, como faz com o perfil:
 *
 *   cobranca: true          só onde há cobrança de ticket (o estacionamento, ou clube e condomínio
 *                           com a chave "cobrança habilitada")
 *   semCobranca: true       só onde NÃO há — é a variante do bloco de cobrança
 *   verticais: [...]        só nessas verticais (o que descreve o G2: endereço, tarifa, capturas)
 *   foraDe: [...]           em todas, menos nessas
 *   tituloSemCobranca       o título da seção onde não há cobrança
 *   linha de tabela         [células] vale sempre; { celulas, cobranca|semCobranca } filtra
 *
 * Regra que não se negocia: no estacionamento, o manual é EXATAMENTE o de antes da Onda 154.
 *
 * BLOCOS
 *
 *   { tipo: 'p',      texto }                      parágrafo
 *   { tipo: 'lista',  itens: [] }                  lista simples
 *   { tipo: 'passos', itens: [] }                  numerada, para procedimento
 *   { tipo: 'tabela', cabecalho: [], linhas: [[]] }
 *   { tipo: 'aviso',  tom: 'dica'|'atencao'|'perigo', titulo, texto }
 *   { tipo: 'figura', src, legenda }               captura de tela (`estreita: true`: tamanho natural, como o menu)
 *   { tipo: 'atalhos', itens: [[tecla, o que faz]] }
 *
 * AS FIGURAS
 *
 * As da raiz são do G2; as de `clube/` são do Ipê e as de `condominio/` do All Suítes (Onda 156b,
 * tiradas pelo Edge sem janela com o roteiro do scratchpad, nomes desfocados na própria página).
 * São capturas reais, com os dados pessoais mascarados — nome, CPF e
 * telefone saem; a placa fica, porque é o assunto do manual e já circula em via
 * pública. Uma figura cujo arquivo ainda não existe aparece como espaço
 * reservado nomeado, em vez de quebrar a página: é melhor o leitor ver "falta
 * esta captura" do que um ícone quebrado.
 */

export const PERFIS = {
  gestor: { id: 'gestor', label: 'Gestor', descricao: 'Responde pelo estacionamento: tarifa, usuários, fechamento e o que envolve dinheiro',
    descricaoSemCobranca: 'Responde pela instalação: usuários, cadastro, configurações e relatórios' },
  operador: { id: 'operador', label: 'Operador', descricao: 'Está no turno: caixa, tickets, pátio e atendimento',
    descricaoSemCobranca: 'Está no turno: portaria, leituras de placa e atendimento' },
};

export const MANUAL_PLATAFORMA = {
  id: 'plataforma',
  titulo: 'Manual da Plataforma',
  subtitulo: 'Estacionamento Comercial — o que se faz pelo computador',
  resumo:
    'A plataforma web é onde se acompanha o pátio, recebe no caixa, confere as leituras e ' +
    'se administra tarifa, mensalista e usuário. Este manual cobre a vertical Estacionamento ' +
    'Comercial. O que se faz pelo celular na guarita está no Manual do App.',

  /**
   * Onda 154: subtítulo e resumo conforme a instalação. No estacionamento, os de sempre.
   * @param {{vertical: string, nome: string, cobranca: boolean}} instalacao
   */
  textosDaInstalacao({ vertical, nome, cobranca }) {
    if (vertical === 'estacionamento') return { subtitulo: this.subtitulo, resumo: this.resumo };
    return {
      subtitulo: `${nome} — o que se faz pelo computador`,
      resumo: cobranca
        ? 'A plataforma web é onde se acompanha a portaria e o pátio, recebe no caixa, confere as leituras e ' +
          `se administra tarifa, cadastro e usuário. Este manual cobre a vertical ${nome}, com a cobrança de ` +
          'ticket habilitada. O que se faz pelo celular na guarita está no Manual do App.'
        : 'A plataforma web é onde se acompanha a portaria, confere as leituras de placa, consulta os relatórios ' +
          'de acesso e se administra o cadastro, a integração com o sistema de gestão, o semáforo da guarita, as ' +
          `câmeras e os usuários. Este manual cobre a vertical ${nome}.`,
    };
  },

  secoes: [
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'comecar',
      titulo: 'Por onde começar',
      blocos: [
        { tipo: 'p', verticais: ['estacionamento'], texto:
          'A plataforma é alcançável de qualquer lugar, sem VPN e sem porta: basta o endereço ' +
          'do seu estacionamento no navegador. No G2 Parking é <b>g2.acessoid.com.br</b>.' },
        { tipo: 'p', foraDe: ['estacionamento'], texto:
          'A plataforma é alcançável de qualquer lugar, sem VPN e sem porta: basta o endereço da sua ' +
          'instalação no navegador, no formato <b>nome.acessoid.com.br</b>.' },
        { tipo: 'p', verticais: ['estacionamento'], texto:
          'Depois de entrar, o menu da esquerda reúne tudo, agrupado por assunto: <b>Operação</b>, ' +
          '<b>Cadastros</b> e <b>Administração</b>. O que você vê no menu depende do seu perfil — ' +
          'o que não aparece, você não alcança, e isso é proposital.' },
        { tipo: 'p', foraDe: ['estacionamento'], texto:
          'Depois de entrar, o menu da esquerda reúne tudo, agrupado por assunto. O que você vê no menu ' +
          'depende do seu perfil — o que não aparece, você não alcança, e isso é proposital. No topo ' +
          'ficam o nome da instalação e a vertical, para quem acessa mais de uma saber em qual está.' },
        { tipo: 'p', foraDe: ['estacionamento'], texto:
          'Os grupos do menu: <b>Operação</b> (Dashboard, Monitoramento, Câmeras, Reconhecimento e Qualidade do ' +
          'OCR); um grupo com o nome da vertical — no clube, <b>Associados</b>; no condomínio, ' +
          '<b>Moradores</b> e <b>Visitantes</b>; nos dois, <b>Relatórios de Acesso</b> —; <b>Integração</b>, com ' +
          'as telas do sistema de gestão; <b>Administração</b>; e <b>Ajuda</b>. Com a cobrança habilitada, aparecem ' +
          'também os módulos de cobrança.' },
        { tipo: 'figura', verticais: ['clube_social'], estreita: true, src: 'clube/menu.png', legenda: 'O menu lateral no clube, visto por um gestor: Operação, Clube Social, Integração e Administração.' },
        { tipo: 'figura', verticais: ['condominio'], estreita: true, src: 'condominio/menu.png', legenda: 'O menu lateral no condomínio: Operação, Condomínio (Moradores, Visitantes e Relatórios de Acesso) e Integração.' },
        { tipo: 'figura', verticais: ['estacionamento'], src: 'menu.png', legenda: 'O menu lateral visto por um gestor: Operação, Cadastros, Administração e Ajuda.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'O menu encolhe',
          texto: 'O botão no topo do menu recolhe a barra para sobrarem só os ícones. Em tela pequena, ou quando a tabela é larga, vale a pena.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'perfis',
      titulo: 'Quem pode o quê',
      blocos: [
        { tipo: 'p', verticais: ['estacionamento'], texto:
          'São quatro perfis. A regra que os organiza é simples: <b>quem está acima alcança o que ' +
          'está abaixo</b>, salvo onde a funcionalidade diz que é de um perfil só.' },
        { tipo: 'tabela', verticais: ['estacionamento'], 
          cabecalho: ['Perfil', 'O que faz'],
          linhas: [
            ['Consulta', 'Lê o Dashboard, os tickets, os relatórios operacionais e a Qualidade do OCR. Não altera nada.'],
            ['Operador', 'Tudo o que a consulta faz, mais o caixa: cobrar, abrir e fechar a gaveta, corrigir leitura, abrir ticket manual, conduzir vistoria do pátio.'],
            ['Administrador', 'Tudo do operador, mais o que envolve dinheiro e regra: isentar, estornar, tarifas, convênios, faturamento de mensalista, relatórios financeiros, encerrar vistoria.'],
            ['Superadministrador', 'Tudo, mais o que erra feio e afeta a captura inteira: usuários, chaves de API, configuração do motor, desenho das zonas de leitura e troca de vertical.'],
          ] },
        { tipo: 'p', foraDe: ['estacionamento'], texto:
          'São cinco perfis. Quatro formam uma escada — <b>quem está acima alcança o que está abaixo</b>, ' +
          'salvo onde a funcionalidade diz que é de um perfil só. A <b>Supervisão</b> fica à parte: ' +
          'acompanha a operação e os relatórios sem alterar nada.' },
        { tipo: 'tabela', foraDe: ['estacionamento'], 
          cabecalho: ['Perfil', 'O que faz'],
          linhas: [
            ['Supervisão', 'Lê o Dashboard, o Monitoramento (sem a câmera ao vivo), a Qualidade do OCR, o cadastro de associados e os Relatórios de Acesso. Não altera nada.'],
            ['Consulta', 'Lê o Dashboard, o Monitoramento com a câmera ao vivo, as Câmeras e a Qualidade do OCR. Não altera nada.'],
            ['Operador', 'Tudo o que a consulta faz, mais corrigir e confirmar leituras e atender na portaria: consulta os associados no clube; no condomínio, mantém moradores e visitantes.'],
            ['Administrador', 'Tudo do operador, mais os Relatórios de Acesso, a edição do cadastro de associados, as câmeras, os usuários da equipe e as configurações.'],
            ['Superadministrador', 'Tudo, mais o que erra feio e afeta a captura inteira: chaves de API, configuração do motor, desenho das zonas de leitura, troca de vertical e a cobrança habilitada.'],
          ] },
        { tipo: 'p', cobranca: true, foraDe: ['estacionamento'], texto:
          'Com a <b>cobrança habilitada</b>, os perfis alcançam também os módulos de cobrança, com as mesmas ' +
          'regras do estacionamento: o operador cobra no caixa, abre e fecha a gaveta, abre ticket manual, ' +
          'conduz a vistoria do pátio e revisa a Quarentena; o administrador isenta, estorna e cuida de tarifas, convênios, ' +
          'faturamento de mensalista e relatórios financeiros.' },
        { tipo: 'aviso', tom: 'atencao', cobranca: true, titulo: 'Isentar e estornar não são do turno',
          texto:
            'As duas liberam ou devolvem dinheiro, e por isso ficam no administrador. Ambas exigem ' +
            'justificativa de pelo menos cinco caracteres, que fica gravada na auditoria com o nome ' +
            'de quem fez. Não é desconfiança do operador: é que perder receita é decisão de quem ' +
            'responde pelo estacionamento.' },
        { tipo: 'figura', src: 'usuarios.png', legenda: 'A tela de Usuários: perfil de cada pessoa e a alçada individual.', perfis: ['gestor'] },
        { tipo: 'p', perfis: ['gestor'], cobranca: true, texto:
          'Quando alguém precisa de uma funcionalidade específica sem virar administrador, existe a ' +
          '<b>alçada individual</b>: em <b>Usuários</b>, você concede aquela funcionalidade e mais ' +
          'nada. É o caminho para o operador de confiança que precisa encerrar a vistoria do pátio ' +
          'sem ganhar poder de mexer em tarifa.' },
        { tipo: 'p', perfis: ['gestor'], semCobranca: true, texto:
          'Quando alguém precisa de uma funcionalidade específica sem mudar de perfil, existe a ' +
          '<b>alçada individual</b>: em <b>Usuários</b>, você concede aquela funcionalidade e mais ' +
          'nada, com motivo e, se quiser, prazo.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'dashboard-acessos',
      titulo: 'Dashboard — os acessos do mês',
      foraDe: ['estacionamento'],
      blocos: [
        { tipo: 'p', texto: 'É a primeira tela depois de entrar. Cada placa reconhecida é um <b>acesso</b>, e o Dashboard responde sobre um <b>mês de referência</b>, escolhido no seletor do topo: as setas navegam e <b>Mês atual</b> volta ao mês corrente. Cada número traz a variação em relação ao mês anterior. O mês corrente se atualiza sozinho a cada minuto.' },
        { tipo: 'figura', verticais: ['clube_social'], src: 'clube/dashboard.png', legenda: 'O Dashboard de acessos do clube: acessos do mês, veículos distintos, entradas, saídas e o movimento por dia e por hora.' },
        { tipo: 'figura', verticais: ['condominio'], src: 'condominio/dashboard.png', legenda: 'O Dashboard de acessos do condomínio, com os acessos negados em destaque.' },
        { tipo: 'tabela', cabecalho: ['Indicador', 'O que conta'], linhas: [
            [
              'Acessos no mês',
              'Todas as leituras registradas, com a média por dia que teve movimento',
            ],
            [
              'Veículos distintos',
              'Quantas placas diferentes passaram, e quantos acessos cada uma fez em média',
            ],
            ['Entradas', 'As leituras das câmeras de entrada'],
            [
              'Saídas',
              'As leituras das câmeras de saída. Zero, com a nota "nenhuma câmera registrando saída", quer dizer que nenhuma câmera de saída registrou no mês',
            ],
            ['Acessos negados', 'As leituras classificadas como acesso negado'],
          ] },
        { tipo: 'p', texto: 'Abaixo vêm o movimento dia a dia, os acessos por hora do dia, <b>Quem acessou</b> (a composição por tipo), os veículos mais frequentes do mês, o volume por câmera e a qualidade da leitura.' },
        { tipo: 'tabela', verticais: ['clube_social'], cabecalho: ['Tipo', 'O que é'], linhas: [
            ['Associados', 'A placa está no cadastro de um associado ativo'],
            [
              'Associados inativos',
              'A placa é de um associado desligado no cadastro',
            ],
            [
              'Visitantes',
              'A placa não está no cadastro — visitante, prestador ou convidado',
            ],
          ] },
        { tipo: 'tabela', verticais: ['condominio'], cabecalho: ['Tipo', 'O que é'], linhas: [
            ['Moradores', 'A placa está cadastrada num morador ativo'],
            [
              'Visitantes',
              'A placa tem autorização de visita válida naquele momento',
            ],
            [
              'Negados',
              'Placa sem cadastro, autorização vencida, morador inativo ou veículo bloqueado',
            ],
          ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'As 24 horas aparecem sempre', texto: 'No gráfico por hora, a madrugada vazia continua no eixo. É o que mostra a diferença entre "não houve movimento" e "a câmera não estava lendo".' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'dashboard',
      titulo: 'Dashboard — o pátio num relance',
      verticais: ['estacionamento'],
      blocos: [
        { tipo: 'p', texto:
          'É a primeira tela depois de entrar. Ela responde às perguntas do dia: quantos veículos ' +
          'estão no pátio agora, quanto entrou hoje, como está o movimento em relação a ontem.' },
        { tipo: 'figura', src: 'dashboard.png', legenda: 'O Dashboard: veículos no pátio, receita do dia e o movimento das últimas horas.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Número que não bate com a realidade',
          texto:
            'Se o total de veículos no pátio parecer alto demais, olhe a <b>Reconciliação</b>. ' +
            'Costuma ser ticket aberto por carro que passou na via sem entrar, ou saída que a ' +
            'câmera não leu — não dinheiro perdido.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'tickets',
      cobranca: true,
      titulo: 'Tickets — a vida de cada veículo',
      blocos: [
        { tipo: 'p', texto:
          'Cada veículo que entra ganha um ticket. A tela mostra todos, com filtro por situação: ' +
          'ativo, pago, finalizado. A ordem padrão é pela entrada mais recente.' },
        { tipo: 'figura', src: 'tickets.png', legenda: 'A lista de tickets, com o filtro de status e a coluna de evidência.' },
        { tipo: 'p', texto:
          'Para ordenar pela saída, clique no cabeçalho <b>Saída</b>: o primeiro clique traz a saída ' +
          'mais recente, o segundo a mais antiga, o terceiro devolve a ordem padrão. Tickets sem ' +
          'saída ficam no fim enquanto essa ordenação estiver ativa.' },
        { tipo: 'p', texto:
          'O ícone de câmera abre as <b>evidências fotográficas</b>: a foto da entrada e, quando já ' +
          'houve, a da saída, cada uma com a câmera e o horário. Em tela larga ficam lado a lado; ' +
          'em tela estreita, uma embaixo da outra. Fecha pelo botão, pelo clique fora ou pela tecla <b>Esc</b>.' },
        { tipo: 'figura', src: 'tickets-evidencia.png', legenda: 'As evidências de entrada e saída do mesmo veículo, lado a lado, com a câmera e o horário de cada uma.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Ticket pago não é ticket encerrado',
          texto:
            'O sistema dá o veículo por saído quando a <b>câmera lê a placa na saída</b>, não quando ' +
            'o cliente paga. Entre pagar e passar pela cancela, o ticket fica em <b>pago</b> e o ' +
            'veículo continua no pátio. É isso que permite distinguir "saiu" de "pagou e ficou".' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'pdv',
      cobranca: true,
      titulo: 'Caixa (PDV) — receber',
      perfis: ['operador', 'gestor'],
      blocos: [
        { tipo: 'p', texto:
          'É a tela do atendimento. Antes de receber o primeiro cliente, é preciso <b>abrir o caixa</b>.' },

        { tipo: 'subtitulo', texto: 'Abrir o caixa' },
        { tipo: 'p', texto:
          'Conte o dinheiro que <b>já está</b> na gaveta — o troco deixado do turno anterior — e ' +
          'informe esse valor como <b>Saldo Inicial</b>. É a partir dele que o sistema apura sobra ' +
          'ou falta no fim do turno. Gaveta vazia, informe zero.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Dinheiro que chega depois não volta aqui',
          texto: 'Entra pelo botão <b>Suprimento</b>, com motivo registrado. O Saldo Inicial é só o da abertura.' },
        { tipo: 'figura', src: 'pdv-abertura.png', legenda: 'A abertura do caixa, com o campo de Saldo Inicial.' },

        { tipo: 'subtitulo', texto: 'Cobrar' },
        { tipo: 'passos', itens: [
          'Ache o veículo: <b>leia o QR do ticket</b> com o leitor de balcão (ou digite os 8 caracteres ' +
            'impressos sob o QR), busque pela <b>placa</b>, ou clique no veículo na lista do pátio.',
          'Confira a placa com o carro e o valor que o sistema calculou — ele já aplica a tarifa vigente no momento da entrada.',
          'Escolha a forma de pagamento.',
          'Confirme.',
        ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'Placa ou ticket, no mesmo campo',
          texto:
            'O leitor de QR de balcão é um leitor USB comum, do tipo "teclado": não precisa de instalação, basta o ' +
            'cursor estar no campo de busca. <b>Prefira o ticket quando o cliente o tiver</b> — o código acha o ' +
            'ticket mesmo que a placa tenha sido lida errada na entrada. Quem perdeu o ticket continua sendo ' +
            'atendido pela placa. O código nunca tem as letras O e I nem os números 0 e 1. Se o ticket lido ' +
            '<b>já foi pago, cancelado ou encerrado</b>, aparece um cartão com a placa e os horários: verde, nada ' +
            'a cobrar; âmbar, o ticket não pode ser cobrado — busque pela placa se o carro ainda está no pátio.' },
        { tipo: 'figura', src: 'pdv-cobranca.png', legenda: 'O atendimento: placa, permanência, valor e as formas de pagamento.' },
        { tipo: 'p', texto:
          'A tela foi feita para operar <b>sem tirar a mão do teclado</b>, que é o que sustenta o ' +
          'ritmo quando há fila:' },
        { tipo: 'atalhos', itens: [
          ['1 a 5', 'escolhe a forma de pagamento — o número aparece no próprio botão'],
          ['Enter', 'confirma o pagamento e, na tela seguinte, chama o próximo atendimento'],
          ['Esc', 'abandona o atendimento em curso e volta para a busca'],
        ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'Digitar a placa não dispara atalho',
          texto: 'Enquanto o cursor estiver num campo de texto, os números digitam normalmente. Só Enter e Esc funcionam ali dentro.' },
        { tipo: 'p', texto:
          '<b>Troco.</b> Ao escolher Dinheiro, os botões abaixo do campo mostram o valor exato e as ' +
          'cédulas com que o cliente costuma pagar, sempre acima da conta. Basta clicar na que ele entregou.' },

        { tipo: 'subtitulo', texto: 'O que dizer ao cliente' },
        { tipo: 'figura', src: 'pdv-comprovante.png', legenda: 'O comprovante, com o "PODE SAIR ATÉ" em destaque — é essa a informação a passar ao cliente.' },
        { tipo: 'aviso', tom: 'perigo', titulo: 'O pagamento não abre a cancela',
          texto:
            'A liberação acontece na saída, quando a câmera lê a placa e confere que aquele ticket ' +
            'está pago. Por isso o comprovante mostra <b>"Pode sair até"</b> com o horário: são ' +
            '15 minutos a partir do pagamento, e é a informação a passar ao cliente. ' +
            'Passada a janela, a cancela não abre nem para quem pagou, e vira atendimento na hora.' },

        { tipo: 'subtitulo', texto: 'A lista de veículos no pátio' },
        { tipo: 'p', texto:
          'Cada veículo aparece num cartão com a placa desenhada como ela é no para-brisa, Mercosul ' +
          'ou padrão anterior. Reconhecer a placa de relance é mais rápido do que ler uma tabela ' +
          'linha a linha — e é o que se faz na prática: olhar o carro parado à frente e procurar ' +
          'aquela placa na tela.' },
        { tipo: 'figura', src: 'pdv-patio.png', legenda: 'Os cartões do pátio, com a placa desenhada como no para-brisa.' },
        { tipo: 'lista', itens: [
          '<b>Faixa verde, "não passa pelo caixa":</b> mensalista, credenciado ou isento. Não precisa selecionar — sai sem pagar permanência. O cabeçalho conta quantos são.',
          '<b>Parados há mais de doze horas</b> ficam recolhidos atrás de um botão: em geral são tickets abertos por carros que passaram na via sem entrar.',
          '<b>Placa com mais de um ticket aberto</b> vem marcada. Cobrar um deixaria o outro em aberto.',
        ] },

        { tipo: 'subtitulo', texto: 'Fechar o caixa' },
        { tipo: 'p', texto:
          'Informar o valor contado na gaveta é <b>obrigatório</b>. É ele que permite apurar sobra ' +
          'ou falta, e a diferença fica gravada na sessão junto com quem contou.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Conte antes de olhar o sistema',
          texto: 'Um fechamento sem conferência de verdade não distingue caixa certo de caixa errado.' },
        { tipo: 'p', texto:
          'Conte <b>só dinheiro</b> — cédulas e moedas. Cartão, PIX e Sem Parar entram na receita do ' +
          'turno, mas não passam pela gaveta e não entram nesta conferência. O resumo mostra esse ' +
          'total à parte, marcado como tal, justamente para não ser confundido com o valor a contar.' },
        { tipo: 'p', texto: 'A conta que o sistema faz:' },
        { tipo: 'codigo', texto:
          'esperado  = Saldo Inicial + vendas em dinheiro + suprimentos − sangrias\n' +
          'diferença = o que você contou − esperado' },
        { tipo: 'p', texto:
          'Na prática: abriu com R$ 200,00 de troco e recebeu R$ 100,00 em dinheiro ao longo do dia? ' +
          'Ao fechar, informe <b>R$ 300,00</b>. Se recebeu também R$ 400,00 em cartão, o número ' +
          'continua R$ 300,00 — cartão não está na gaveta.' },
        { tipo: 'figura', src: 'pdv-fechamento.png', legenda: 'O fechamento do caixa, com o esperado, o contado e a diferença.' },

        { tipo: 'subtitulo', texto: 'Isentar e estornar', perfis: ['gestor'] },
        { tipo: 'p', perfis: ['gestor'], texto:
          '<b>Isentar</b> libera a saída sem cobrar. Por isso não fica ao lado do botão de cobrar: ' +
          'aparece abaixo, separado, com o aviso do que exige. <b>Estornar</b> não exclui o ' +
          'histórico — o valor volta para a sessão de caixa <b>onde o pagamento entrou</b>, e não ' +
          'para a de quem estorna. É o que mantém os dois fechamentos corretos.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'monitoramento',
      titulo: 'Monitoramento e Câmeras',
      blocos: [
        { tipo: 'p', texto:
          'O <b>Monitoramento</b> mostra o que cada câmera está vendo agora, com a última placa lida ' +
          'e a confiança da leitura. É onde se olha primeiro quando alguém diz que "o sistema não ' +
          'está lendo".' },
        { tipo: 'figura', src: 'monitoramento.png', legenda: 'O Monitoramento em tempo real, com a última leitura de cada câmera.' },
        { tipo: 'p', texto:
          'A tela de <b>Câmeras</b> lista o que está cadastrado e o estado de cada uma. Uma câmera ' +
          'que perdeu conexão aparece marcada, e o sistema tenta reconectar sozinho com intervalo ' +
          'crescente.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'As zonas de leitura são do superadministrador',
          texto:
            'O desenho da zona (ROI) define onde a placa é aceita. Um traço errado faz o sistema ' +
            'parar de ler, ou passar a ler carros da rua. Por isso é a única coisa da tela de ' +
            'câmeras que fica fora do alcance do administrador.' },
        { tipo: 'p', foraDe: ['estacionamento'], perfis: ['operador'], texto:
          'No perfil <b>Supervisão</b>, o botão <b>Ao Vivo</b> não aparece e o item Câmeras não está no menu: o ' +
          'Monitoramento mostra a última leitura de cada câmera, sem o vídeo.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'ocr',
      titulo: 'Qualidade do OCR — conferir as leituras',
      blocos: [
        { tipo: 'p', texto:
          'É onde se vê, leitura por leitura, o que a câmera entendeu — com a foto, o recorte da ' +
          'placa e a confiança de cada caractere. Serve para duas coisas: corrigir o que saiu errado ' +
          'e medir se o reconhecimento está indo bem.' },
        { tipo: 'figura', src: 'ocr-qualidade.png', legenda: 'A tela Qualidade do OCR, com a foto, o recorte e a leitura de cada passagem.' },
        { tipo: 'passos', itens: [
          'Abra a leitura para ver a foto e o recorte.',
          'Se a placa está certa, clique em <b>Confirmar leitura</b>. Um toque, não abre nada.',
          'Se está errada, clique em corrigir e digite a placa correta.',
        ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'Confirmar vale tanto quanto corrigir',
          texto:
            'Uma leitura confirmada por quem estava olhando o carro é <b>verdade de terreno</b>: é o ' +
            'que permite medir a efetividade real do reconhecimento, em vez de confiar na confiança ' +
            'que o próprio motor declara. E a medição já mostrou que ela não prediz o erro — em 84% ' +
            'dos casos errados o motor estava 95% seguro ou mais.' },
        { tipo: 'aviso', tom: 'dica', foraDe: ['estacionamento'], titulo: 'A correção vale para o acesso',
          texto:
            'Corrigir uma leitura reescreve o registro com a placa certa, recalcula o tipo de acesso, avisa o sistema ' +
            'de gestão com um evento de correção e acende o semáforo de novo. A leitura original fica guardada e ' +
            'continua contando para medir o reconhecimento.' },
        { tipo: 'p', foraDe: ['estacionamento'], perfis: ['operador'], texto:
          'No perfil <b>Supervisão</b> a tela é só para ver: o detalhe da leitura abre com a foto e o recorte, sem o ' +
          'campo de corrigir.' },
        { tipo: 'p', texto:
          'Ao abrir uma leitura você vê também <b>Proporção · posição</b>. A proporção é a largura ' +
          'dividida pela altura do retângulo em volta da placa: quanto menor, mais "quadrada" ela ' +
          'apareceu — o que acontece quando o veículo está atravessado. A posição vai de 0 (borda ' +
          'esquerda) a 1 (borda direita). Os dois números explicam por que uma leitura saiu errada.' },
        { tipo: 'subtitulo', texto: 'Aguardando revisão: por onde começar' },
        { tipo: 'p', cobranca: true, texto:
          'O selo amarelo <b>revisar</b> marca as leituras em que o erro costuma se esconder. O filtro ' +
          '<b>Só aguardando revisão</b> lista só elas, e o cartão no topo diz quantas há. O selo nunca muda o ' +
          'registro — o ticket abre do mesmo jeito; ele só pede um olhar. São dois motivos:' },
        { tipo: 'p', semCobranca: true, texto:
          'O selo amarelo <b>revisar</b> marca as leituras em que o erro costuma se esconder. O filtro ' +
          '<b>Só aguardando revisão</b> lista só elas, e o cartão no topo diz quantas há. O selo nunca muda o ' +
          'registro — o acesso é registrado do mesmo jeito; ele só pede um olhar. São dois motivos:' },
        { tipo: 'tabela',
          cabecalho: ['Motivo', 'O que é'],
          linhas: [
            ['placa de lado', 'Abertura registrada com <b>uma única leitura</b>, com a placa pequena e bem de lado — o veículo que passa colado à câmera.'],
            ['placa pequena e de viés', 'Na <b>câmera de entrada</b>, placa abaixo de 110 px vista de viés — o carro lido ainda atravessado, no começo da conversão. Medido sobre 2.366 aberturas revisadas: de frente, essas placas pequenas erram 1 em 74; de viés, <b>15 em 31</b>. Cerca de uma por dia, e metade está errada de fato.'],
          ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'É onde o tempo de revisão rende mais',
          texto:
            'Acima de 110 px o reconhecimento erra 0,6% das vezes; nas leituras com o selo, perto de metade. ' +
            'Se só houver tempo para revisar algumas leituras por dia, comece por estas.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'reconciliacao',
      cobranca: true,
      titulo: 'Reconciliação — o que ficou pendente',
      blocos: [
        { tipo: 'p', texto:
          'Reúne o que não fechou sozinho: ticket aberto há tempo demais, veículo que pagou e não ' +
          'foi visto sair, entrada sem saída correspondente. É a tela que impede que essas coisas ' +
          'virem número inflado no Dashboard.' },
        { tipo: 'figura', src: 'reconciliacao.png', legenda: 'A Reconciliação, com os tickets que pedem decisão.' },
        { tipo: 'p', texto:
          'Boa parte do que aparece aqui não é problema: é carro que passou na via e foi lido pela ' +
          'câmera de entrada sem entrar. A <b>Janela de Abandono</b> define a partir de quanto tempo ' +
          'um ticket é considerado abandonado.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Reentrada é sinal de saída não lida',
          texto:
            'Quando a mesma placa aparece entrando de novo com o ticket anterior ainda aberto, quase ' +
            'sempre significa que a saída não foi reconhecida. É o rastro mais confiável desse tipo ' +
            'de falha.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'inventario',
      cobranca: true,
      titulo: 'Inventário do Pátio — a vistoria',
      blocos: [
        { tipo: 'p', texto:
          'A vistoria compara o que o sistema acha que está no pátio com o que de fato está lá. ' +
          'Ela se faz andando entre os carros, com o celular na mão — por isso a condução é do ' +
          '<b>operador</b> e o app da guarita tem a tela própria para isso.' },
        { tipo: 'figura', src: 'inventario.png', legenda: 'O Inventário do Pátio na plataforma, com a vistoria em andamento.' },
        { tipo: 'passos', itens: [
          'Abra uma vistoria.',
          'Percorra o pátio registrando as placas encontradas — pelo app, ou aqui pela plataforma.',
          'Ao fim, o sistema mostra o que sobrou e o que faltou.',
          'O <b>gestor</b> encerra a vistoria, e aí os tickets são baixados em lote.',
        ] },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Encerrar mexe em dinheiro',
          texto:
            'Encerrar a vistoria baixa tickets em lote. Por isso conduzir é do operador e encerrar é ' +
            'do gestor. Quem precisa dos dois sem ser gestor recebe <b>alçada individual</b>, que ' +
            'concede essa funcionalidade e mais nada.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'cadastros',
      cobranca: true,
      titulo: 'Cadastros — quem não paga por hora',
      perfis: ['gestor', 'operador'],
      blocos: [
        { tipo: 'subtitulo', texto: 'Mensalistas' },
        { tipo: 'p', texto:
          'Plano, assinatura, uma ou mais placas autorizadas, carteira e faturas. O cancelamento ' +
          'encerra no fim da competência — não no dia.' },
        { tipo: 'figura', src: 'mensalistas.png', legenda: 'A tela de Mensalistas, com plano, placas e faturas.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'O faturamento automático não liga sozinho', perfis: ['gestor'],
          texto:
            'Antes de habilitá-lo, o administrador precisa definir vencimento, carência, multa, ' +
            'juros, ação para inadimplência e suspensão. Sem esses parâmetros, a cobrança automática ' +
            'permanece desligada — e é melhor assim do que cobrar errado.' },

        { tipo: 'subtitulo', texto: 'Credenciados' },
        { tipo: 'p', texto:
          'Podem ter várias placas e histórico de vínculos. O padrão é acesso livre 24×7; quando ' +
          'necessário, define-se vigência, dias e horários. Remover uma placa <b>encerra a vigência ' +
          'dela</b>, sem apagar o histórico.' },

        { tipo: 'figura', src: 'credenciados.png', legenda: 'A tela de Credenciados, com as placas autorizadas e a vigência de cada vínculo.' },

        { tipo: 'subtitulo', texto: 'Convênios', perfis: ['gestor'] },
        { tipo: 'p', perfis: ['gestor'], texto:
          'Código, desconto ou isenção, vigência, cota diária e limite de permanência. A aplicação é ' +
          'conferida no PDV, no momento do atendimento. Alterações e reversões são do administrador.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'tarifas',
      cobranca: true,
      titulo: 'Tarifas',
      perfis: ['gestor'],
      blocos: [
        { tipo: 'p', verticais: ['estacionamento'], texto:
          'A tarifa define o que se cobra por permanência. A vigente no G2:' },
        { tipo: 'tabela', verticais: ['estacionamento'],
          cabecalho: ['Regra', 'Valor'],
          linhas: [
            ['Carência de permanência', '10 minutos'],
            ['Primeira hora', 'R$ 7,00'],
            ['Hora adicional', 'R$ 3,00, arredondada para cima'],
            ['Tolerância pós-pagamento', '15 minutos'],
            ['Teto diário', 'Desativado'],
          ] },
        { tipo: 'p', verticais: ['estacionamento'], texto: 'Uma permanência de 61 minutos resulta em R$ 10,00.' },
        { tipo: 'p', foraDe: ['estacionamento'], texto:
          'A tarifa define o que se cobra por permanência: carência, primeira hora, hora adicional, ' +
          'tolerância depois do pagamento e teto diário. A tela mostra a regra vigente e a de cada período.' },
        { tipo: 'figura', src: 'tarifas.png', legenda: 'A tela de Tarifas, com as regras e a vigência de cada uma.' },
        { tipo: 'aviso', tom: 'perigo', titulo: 'Mudar a tarifa não muda o que já entrou',
          texto:
            'Tickets em aberto preservam a regra vigente no momento da <b>entrada</b>. Quem entrou ' +
            'antes da mudança paga o preço antigo, e isso é deliberado: cobrar do cliente uma tarifa ' +
            'que não existia quando ele estacionou é o tipo de coisa que gera discussão no caixa.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'relatorios',
      cobranca: true,
      titulo: 'Relatórios',
      blocos: [
        { tipo: 'p', texto:
          'Os <b>relatórios operacionais</b> — movimento, permanência, ocupação — estão ao alcance ' +
          'de quem consulta. Os <b>financeiros</b> são do administrador.' },
        { tipo: 'figura', src: 'relatorios.png', legenda: 'A tela de Relatórios, com o período e o tipo de relatório.' },
        { tipo: 'p', perfis: ['gestor'], texto:
          'O <b>Log de Cancelas</b> registra cada abertura, com origem e, quando houve intervenção ' +
          'manual, a justificativa e quem a deu. É a tela para a pergunta "por que essa cancela abriu?".' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'associados',
      titulo: 'Associados — o cadastro do clube',
      verticais: ['clube_social'],
      blocos: [
        { tipo: 'p', texto: 'Em <b>Clube Social → Associados</b> fica o cadastro que o sistema de gestão do clube envia pela API: código, nome, situação, quantos veículos cada associado tem e de onde o registro veio — a chave de API que o carregou, ou "cadastro local".' },
        { tipo: 'p', texto: 'A busca aceita nome, código <b>e placa</b>: na portaria, a placa costuma ser a única coisa que se tem em mãos. <b>Abrir</b> mostra os veículos do associado, quando o sistema de gestão o atualizou pela última vez e as últimas passagens das placas dele.' },
        { tipo: 'figura', src: 'clube/associados.png', legenda: 'A tela Associados: código, nome, situação, veículos e a origem de cada registro (nomes e códigos mascarados).' },
        { tipo: 'aviso', tom: 'dica', titulo: 'O acesso aparece no nome do titular', texto: 'Quando o sistema de gestão envia só o titular de cada veículo, o carro da família dirigido por um dependente aparece no nome do titular. É o desenho, não um erro.' },
        { tipo: 'subtitulo', texto: 'Corrigir o cadastro', perfis: ['gestor'] },
        { tipo: 'p', perfis: ['gestor'], texto: 'O administrador corrige o nome e a situação do associado, e a placa, a marca, o modelo e a situação de cada veículo. Os demais perfis só consultam.' },
        { tipo: 'aviso', tom: 'atencao', perfis: ['gestor'], titulo: 'A próxima carga substitui o que você alterar', texto: 'O dono do cadastro é o sistema de gestão do clube. A plataforma registra a correção e mostra "editado aqui", com o seu usuário e a data, mas a carga seguinte sobrescreve. Para uma correção definitiva, corrija lá.' },
        { tipo: 'p', perfis: ['gestor'], texto: 'Uma placa ativa pertence a um único veículo. A plataforma recusa pôr numa ficha uma placa que já está ativa em outra, e diz qual: é o que permite à leitura da câmera saber a quem atribuir o acesso.' },
        { tipo: 'p', texto: 'Associado que chegou e não está no cadastro? Confira a última carga em <b>Integração → Cargas da Integração</b>: ela diz o que o sistema de gestão enviou e o que foi recusado.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'moradores',
      titulo: 'Moradores e veículos',
      verticais: ['condominio'],
      blocos: [
        { tipo: 'p', texto: 'Em <b>Condomínio → Moradores</b>, cada morador tem nome, unidade, bloco ou torre, documento, contato, situação e os veículos dele — placa, marca, modelo, cor e ano. <b>Novo Morador</b> cadastra; dentro do morador, <b>Adicionar Veículo</b> inclui uma placa. A busca aceita nome, unidade e bloco.' },
        { tipo: 'figura', src: 'condominio/moradores.png', legenda: 'A tela Moradores: cada cartão com bloco, unidade, situação e veículos (nomes mascarados).' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Unidade e bloco', texto: 'A mesma numeração se repete em blocos diferentes: a 101 do bloco A e a 101 do bloco B são unidades distintas. Preencha o bloco sempre que o condomínio tiver mais de um — uma unidade sem bloco cuja numeração já exista em outro bloco é recusada, com a mensagem dizendo o que fazer. Os relatórios mostram bloco e unidade juntos: <b>A-101</b>.' },
        { tipo: 'p', texto: 'A placa de morador ativo é liberada na portaria; morador inativo ou veículo bloqueado gera acesso negado.' },
        { tipo: 'p', texto: 'Quando o cadastro vem do sistema de gestão do condomínio, o morador aparece marcado <b>via API</b>; alterado na tela, <b>editado aqui</b>. A carga seguinte do sistema de gestão pode sobrescrever o que for alterado à mão.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'visitantes',
      titulo: 'Visitantes — autorizar a visita',
      verticais: ['condominio'],
      blocos: [
        { tipo: 'p', texto: 'Em <b>Condomínio → Visitantes</b>, <b>Nova Autorização</b> registra quem pode entrar e quando: o nome do visitante, a placa, o morador que autoriza, o tipo — dia único, período de vários dias ou recorrente, para prestador de serviço —, o início e o fim da validade e o motivo da visita.' },
        { tipo: 'figura', src: 'condominio/visitantes.png', legenda: 'Nova Autorização: o visitante, a placa, o morador que autoriza, o tipo e a validade.' },
        { tipo: 'p', texto: 'Na lista, cada autorização aparece como <b>Ativo</b>, <b>Expirado</b> ou <b>Cancelado</b>, e a que vence hoje vem marcada. Só a ativa pode ser editada ou cancelada. A placa não muda depois de salva: para outra placa, crie outra autorização.' },
        { tipo: 'p', texto: 'A aba <b>Acessos Negados</b> lista as tentativas de placas que não são de morador nem de visitante autorizado, com o motivo: não cadastrado, autorização expirada, morador inativo ou veículo bloqueado. <b>Confirmar</b> marca que a portaria viu; o número ao lado da aba conta as que ainda não foram confirmadas.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'O motivo diz o que fazer', texto: '"Não cadastrado" é caso de cadastro. "Autorização expirada" é caso de pedir uma autorização nova ao morador.' },
        { tipo: 'p', texto: 'Quando há integração com o sistema de gestão do condomínio, as autorizações podem chegar por ele; a tela continua servindo ao que a portaria faz na hora.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'relatorios-acesso',
      titulo: 'Relatórios de Acesso',
      foraDe: ['estacionamento'],
      blocos: [
        { tipo: 'p', texto: 'No menu, dentro do grupo com o nome da vertical. Alcançam o administrador e a Supervisão. Cada aba responde sobre um período, com filtros de data, horário e câmera e a busca por placa ou nome; na Movimentação Geral, também o tipo de acesso.' },
        { tipo: 'figura', verticais: ['clube_social'], src: 'clube/relatorios-acesso.png', legenda: 'Relatórios de Acesso no clube: os cartões do topo e a Movimentação Geral, com o selo "na tolerância" nas saídas (nomes mascarados).' },
        { tipo: 'figura', verticais: ['condominio'], src: 'condominio/relatorios-acesso.png', legenda: 'Relatórios de Acesso no condomínio, com as abas Por Morador e Por Unidade (nomes mascarados).' },
        { tipo: 'tabela', verticais: ['clube_social'], cabecalho: ['Aba', 'O que responde'], linhas: [
            [
              'Movimentação Geral',
              'Cada acesso do período: placa, câmera, horário, tipo, permanência e a foto',
            ],
            [
              'Por Associado',
              'Quantos acessos cada associado teve, com as saídas na tolerância',
            ],
            [
              'Visitantes',
              'Os acessos de placas fora do cadastro, com o resultado na hora do acesso',
            ],
            ['Acessos Negados', 'As leituras negadas'],
            [
              'Frequência por Veículo',
              'Os veículos que passaram pelo menos N vezes, ou em pelo menos N dias, no período',
            ],
          ] },
        { tipo: 'tabela', verticais: ['condominio'], cabecalho: ['Aba', 'O que responde'], linhas: [
            [
              'Movimentação Geral',
              'Cada acesso do período: placa, câmera, horário, tipo, permanência e a foto',
            ],
            [
              'Por Morador',
              'Quantos acessos cada morador teve, com as saídas na tolerância',
            ],
            [
              'Visitantes',
              'Os acessos de visitantes, com o resultado na hora do acesso',
            ],
            ['Acessos Negados', 'As leituras negadas, com o motivo'],
            ['Por Unidade', 'Os acessos somados por bloco e unidade'],
            [
              'Frequência por Veículo',
              'Os veículos que passaram pelo menos N vezes, ou em pelo menos N dias, no período',
            ],
          ] },
        { tipo: 'p', verticais: ['clube_social'], texto: 'Na Movimentação Geral, cada leitura traz um de três tipos: <b>Associado</b> (a placa está no cadastro, com o código e o nome do titular), <b>Visitante</b> (a placa não está no cadastro) e <b>Associado inativo</b>. Os cartões do topo contam os três. Uma placa que entra no cadastro no meio do dia vira "Associado" a partir da carga seguinte; as leituras anteriores ficam como foram classificadas na hora.' },
        { tipo: 'subtitulo', texto: 'Saídas na tolerância' },
        { tipo: 'p', texto: 'Toda saída mostra, na coluna <b>Permanência</b>, quanto tempo o veículo ficou: a diferença para a última entrada lida da mesma placa nas 24 horas anteriores. Quando esse tempo cabe na <b>tolerância de permanência</b> (Configurações → Operação; 15 minutos por padrão), a saída recebe o selo <b>na tolerância</b>: é o veículo que entrou e saiu sem usar o estacionamento. O filtro <b>Só saídas na tolerância</b> lista só elas.' },
        { tipo: 'aviso', tom: 'dica', titulo: '"Entrada não lida"', texto: 'Uma saída sem entrada lida nas 24 horas anteriores aparece assim, sem selo: o sistema não inventa a permanência. Muitas delas num dia apontam a câmera de entrada fora do lugar.' },
        { tipo: 'subtitulo', texto: 'Visitantes: o resultado na hora do acesso' },
        { tipo: 'p', texto: 'A última coluna diz o que valia <b>no momento da leitura</b>, pelas autorizações de visita: <b>Autorizado</b> (havia autorização para aquela placa naquele horário, ou é a saída de uma visita que entrou autorizada), <b>Autorização vencida</b> (a placa já teve autorização, mas ela não valia naquele momento) e <b>Sem autorização</b> (a placa nunca teve autorização até ali). Os cartões do topo contam os três.' },
        { tipo: 'subtitulo', texto: 'Exportar e contar a frequência' },
        { tipo: 'p', texto: '<b>Exportar CSV</b> leva o período inteiro, não só a página visível, nas abas Movimentação Geral, Visitantes e Acessos Negados. A planilha abre no Excel com a acentuação certa.' },
        { tipo: 'p', texto: 'Na <b>Frequência por Veículo</b>, um acesso é uma <b>entrada</b>: a saída do mesmo veículo aparece em coluna própria, e a leitura negada não conta. A tabela traz duas contagens, porque cinco idas num sábado são cinco acessos e um dia com acesso. <b>Contar por</b> escolhe qual delas o "no mínimo N" compara; o padrão é <b>dias com acesso</b>. Placa sem cadastro aparece como <i>Não cadastrado</i>.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Leituras que não contam', texto: 'A leitura que a plataforma reconheceu como variante de uma placa recém-registrada — a mesma passagem lida com um caractere trocado — não aparece nos relatórios nem no Dashboard. Ela continua visível na Qualidade do OCR, para medir o erro.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'quarentena',
      titulo: 'Quarentena e Log de Cancelas',
      // Onda 156: só recebe ticket aberto que saiu sem leitura; sem cobrança, não existe.
      cobranca: true,
      blocos: [
        { tipo: 'p', texto:
          'A <b>Quarentena</b> recolhe as ocorrências que o sistema não soube decidir sozinho — ' +
          'leitura que destoa do padrão, saída vista pela câmera de entrada, veículo que aparece ' +
          'onde não devia. Nada aqui cobra ou libera nada por conta própria: é uma fila para alguém olhar.' },
        { tipo: 'figura', src: 'quarentena.png', legenda: 'A Quarentena, com as ocorrências que pedem uma segunda olhada.' },
        { tipo: 'p', cobranca: true, texto:
          'O <b>Log de Cancelas</b> registra cada abertura, com a origem — reconhecimento, comando ' +
          'manual, laço indutivo — e, quando houve intervenção de alguém, a justificativa e o nome. ' +
          'É a tela para a pergunta "por que essa cancela abriu?".' },
        { tipo: 'figura', cobranca: true, src: 'log-cancelas.png', legenda: 'O Log de Cancelas: cada abertura com a sua origem.' },
        { tipo: 'aviso', tom: 'dica', cobranca: true, titulo: 'É aqui que uma dúvida vira resposta',
          texto:
            'Quando o cliente diz que a cancela não abriu, ou que abriu sozinha, estas duas telas ' +
            'contam o que de fato aconteceu — com horário e origem.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'semaforo',
      titulo: 'Semáforo da guarita',
      foraDe: ['estacionamento'],
      blocos: [
        { tipo: 'p', verticais: ['clube_social'], texto: 'Na bancada da guarita pode haver uma controladora com lâmpadas. A cada placa lida pela câmera de entrada, acende <b>verde</b> se o veículo é de associado ativo, e <b>vermelho, com dois bipes</b>, se não é — associado inativo, visitante, veículo bloqueado ou não cadastrado.' },
        { tipo: 'p', verticais: ['condominio'], texto: 'Na bancada da guarita pode haver uma controladora com lâmpadas. A cada placa lida pela câmera de entrada, acende <b>verde</b> se o veículo é de morador, e <b>vermelho, com dois bipes</b>, se não é — visitante (mesmo com visita autorizada), veículo bloqueado ou não cadastrado.' },
        { tipo: 'p', texto: 'Na câmera de <b>saída</b>, acende <b>âmbar, com um bipe curto</b>, quando o veículo sai dentro da tolerância de permanência — o mesmo selo "na tolerância" dos Relatórios de Acesso, na hora. A cor fica acesa por alguns segundos, e uma leitura nova sobrepõe a anterior.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'O sinal não abre nada', texto: 'Ele diz "é dos nossos ou não", não "pode entrar": a decisão de liberar continua sendo da portaria. Sem broker configurado, ou com a controladora fora da rede, nada acende e a leitura segue normal.' },
        { tipo: 'subtitulo', texto: 'Ligar e testar', perfis: ['gestor'] },
        { tipo: 'passos', perfis: ['gestor'], itens: [
            'Em <b>Câmeras</b>, edite a câmera, marque <b>Semáforo da guarita</b> e informe o <b>código da controladora</b> — o mesmo gravado na placa, por exemplo GUARITA-01. Na câmera de saída, use o mesmo código da entrada.',
            'No card da câmera, <b>Testar verde</b> e <b>Testar vermelho</b> (ou <b>Testar âmbar</b>, na saída) acendem a lâmpada por alguns segundos, mesmo com o semáforo desligado: é como se confere a fiação.',
            'Em <b>Configurações → Integração → Semáforo da guarita</b> ficam o broker, o usuário, a senha da controladora, o tempo da cor e o bipe, e a lista das controladoras vivas, com IP e versão do firmware.',
          ] },
        { tipo: 'figura', perfis: ['gestor'], verticais: ['clube_social'], src: 'clube/semaforo.png', legenda: 'Configurações → Semáforo da guarita: a plataforma conectada ao broker e as controladoras vivas, com IP e firmware.' },
        { tipo: 'subtitulo', texto: 'Gravar a controladora', perfis: ['gestor'] },
        { tipo: 'p', perfis: ['gestor'], texto: 'O cartão <b>Firmware do semáforo da guarita</b>, no alto desta Central, prepara o programa: toque em <b>Preparar</b>, escolha a câmera, diga se a controladora terá IP fixo ou DHCP (com DHCP, o IP vira a reserva) e informe IP, gateway e máscara da rede. Com a placa no USB e o Chrome ou o Edge, <b>Gravar pela USB</b> compila no servidor e grava daqui, em uns 15 segundos. Sem esse botão, <b>Baixar zip</b> traz o programa e o passo a passo para o Arduino IDE.' },
        { tipo: 'aviso', tom: 'atencao', perfis: ['gestor'], titulo: 'O zip carrega a senha da controladora', texto: 'Apague o arquivo depois de gravar. Antes do primeiro uso, grave a senha da controladora em Configurações → Semáforo da guarita.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'integracao',
      titulo: 'Integração com o sistema de gestão',
      foraDe: ['estacionamento'],
      blocos: [
        { tipo: 'p', texto: 'A cada placa registrada, a plataforma entrega na hora um evento ao sistema de gestão da instalação — o <b>webhook</b> —, com a placa, a câmera, o horário, o tipo de acesso e o link da foto. O evento é gravado antes da entrega: se o sistema de gestão estiver fora do ar, ele espera na fila e é reenviado sozinho quando o destino volta, com a passagem mais recente na frente.' },
        { tipo: 'p', texto: 'Quando uma leitura é corrigida — pela própria plataforma ou na Qualidade do OCR —, o evento errado que ainda estava na fila é descartado, e sai um evento novo, marcado como correção, com a placa certa.' },
        { tipo: 'figura', verticais: ['clube_social'], src: 'clube/integracao.png', legenda: 'Integração e Outbox: a fila de eventos no topo e o endereço do webhook logo abaixo.' },
        { tipo: 'tabela', cabecalho: ['Tela', 'Para quê', 'Quem alcança'], linhas: [
            [
              'Integração e Outbox',
              'Endereço do webhook, segredo de assinatura, tentativas, link das fotos e retenção; <b>Testar webhook</b>; a fila de eventos, com o histórico de cada tentativa e o reenvio',
              'Administrador',
            ],
            [
              'Cargas da Integração',
              'Cada carga do cadastro enviada pelo sistema de gestão: o que entrou, o que foi recusado e por quê',
              'Operador em diante',
            ],
            [
              'Log de Requisições',
              'As chamadas que o sistema de gestão fez à nossa API, com o código de resposta e o tempo',
              'Consulta em diante',
            ],
            [
              'Documentação da Integração',
              'O contrato que o integrador segue, com o botão para salvar em PDF',
              'Consulta em diante',
            ],
            [
              'Chaves de API',
              'A credencial do sistema de gestão; a chave inteira aparece uma vez só, na criação',
              'Superadministrador',
            ],
          ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'Carga "com sucesso" pode deixar gente de fora', texto: 'A plataforma responde item a item o que aceitou e o que recusou. Abra a carga em <b>Cargas da Integração</b>: a lista de recusados, com a causa de cada um, é o que se envia a quem mantém o sistema de gestão.' },
        { tipo: 'aviso', tom: 'dica', perfis: ['gestor'], titulo: 'O integrador diz que "a API não respondeu"?', texto: 'Olhe o <b>Log de Requisições</b>. A chamada não aparece: ela não chegou até nós. Aparece com 401 ou 403: chegou, mas a chave não foi aceita ou não tem o escopo daquele endereço. Aparece com sucesso: chegou e foi aceita, e o problema está no conteúdo enviado.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'configuracoes',
      titulo: 'Configurações',
      perfis: ['gestor'],
      blocos: [
        { tipo: 'p', texto:
          'A tela tem um <b>menu lateral</b> e mostra <b>uma seção por vez</b>, agrupadas pela finalidade. ' +
          'O endereço da seção aberta fica na barra do navegador: dá para guardar ou enviar o link.' },
        { tipo: 'tabela', verticais: ['estacionamento'], 
          cabecalho: ['Grupo', 'Seção', 'O que tem lá'],
          linhas: [
            ['Operação', 'Operação', 'Tipo de operação, Modo de Operação Paralela, Modo Câmera Única'],
            ['', 'Permanência e cobrança', 'Tolerância de saída após o pagamento e Janela de Abandono (só no estacionamento)'],
            ['', 'Impressão do ticket', 'Onde imprimir, o papel, cabeçalho e rodapé (só no estacionamento)'],
            ['', 'Evidências e armazenamento', 'Por quanto tempo guardar fotos e recortes, e o que gravar'],
            ['Reconhecimento', 'Captura e motor', 'Intervalo de captura, os dois tempos-limite, CPU/GPU'],
            ['', 'Registro de passagem', 'O modo de filtragem, a consolidação por passagem, o intervalo entre registros da mesma placa, a largura mínima da placa'],
            ['', 'Zona de observação', 'A segunda zona da câmera de entrada'],
            ['', 'Confiança e luminosidade', 'Os limiares de confiança e as faixas de brilho que escolhem qual deles vale'],
            ['', 'OCR e pré-processamento', 'Confiança por caractere, redução de ruído, classificador de layout'],
            ['Integração', 'Cancela e webhooks', 'URL do webhook, teste e a referência de payloads'],
            ['Sistema', 'Vigias e alertas', 'O vigia do fluxo de entrada (só no estacionamento) e o tempo para uma câmera ser dada como sem sinal'],
            ['', 'App da guarita', 'A versão publicada do app e o aviso que aparece nos aparelhos (só no estacionamento)'],
          ] },
        { tipo: 'tabela', foraDe: ['estacionamento'], 
          cabecalho: ['Grupo', 'Seção', 'O que tem lá'],
          linhas: [
            ['Operação', 'Operação', 'Nome do cliente, Tipo de operação, Modo de Operação Paralela, Modo Câmera Única, a tolerância dos Relatórios de Acesso e a Cobrança habilitada (esta, só para o superadministrador)'],
            { cobranca: true, celulas: ['', 'Permanência e cobrança', 'Tolerância de saída após o pagamento e Janela de Abandono'] },
            { cobranca: true, celulas: ['', 'Impressão do ticket', 'Onde imprimir, o papel, cabeçalho e rodapé'] },
            ['', 'Evidências e armazenamento', 'Por quanto tempo guardar fotos e recortes, e o que gravar'],
            ['Reconhecimento', 'Captura e motor', 'Intervalo de captura, os dois tempos-limite, CPU/GPU'],
            ['', 'Registro de passagem', 'O modo de filtragem, a consolidação por passagem, o intervalo entre registros da mesma placa, a largura mínima da placa'],
            ['', 'Confiança e luminosidade', 'Os limiares de confiança e as faixas de brilho que escolhem qual deles vale'],
            ['', 'OCR e pré-processamento', 'Confiança por caractere, redução de ruído, classificador de layout'],
            ['Integração', 'Entrega dos eventos ao sistema de gestão', 'Por quanto tempo a plataforma insiste quando o sistema de gestão está fora do ar, e o que faz quando ele volta'],
            ['', 'Cancela e webhooks', 'URL do webhook, teste e a referência de payloads'],
            ['', 'Semáforo da guarita', 'O broker, a senha da controladora e o tempo e o bipe do sinal'],
            { semCobranca: true, celulas: ['Sistema', 'Vigias e alertas', 'O tempo para uma câmera ser dada como sem sinal e os avisos pelo Telegram'] },
            { cobranca: true, celulas: ['Sistema', 'Vigias e alertas', 'O vigia do fluxo de entrada, o tempo para uma câmera ser dada como sem sinal e os avisos pelo Telegram'] },
            { cobranca: true, celulas: ['', 'App da guarita', 'A versão publicada do app e o aviso que aparece nos aparelhos'] },
          ] },
        { tipo: 'subtitulo', cobranca: true, texto: 'O vigia do fluxo de entrada' },
        { tipo: 'p', cobranca: true, texto:
          'É o alarme que avisa quando a entrada <b>para de abrir tickets com tudo funcionando</b>. Em ' +
          '<b>Sistema → Vigias e alertas</b> a tela mostra o veredito de agora — entradas, saídas, quantas saídas ' +
          'sem ticket e o normal do horário — e os números que calibram o alarme. O vermelho exige os dois lados: ' +
          'saídas sem ticket em alta <b>e</b> a entrada sem abrir tickets agora.' },
        { tipo: 'aviso', tom: 'dica', cobranca: true, titulo: 'O alarme está incomodando à toa?',
          texto:
            'Não desligue: aumente as <b>saídas mínimas</b> ou o <b>movimento mínimo</b>, que são o que impede alarme em ' +
            'horário de pouco movimento. Uma mudança leva até um minuto para aparecer no painel de saúde.' },
        { tipo: 'subtitulo', cobranca: true, texto: 'Publicar uma versão nova do app da guarita' },
        { tipo: 'passos', cobranca: true, itens: [
          'Publique o instalador novo no servidor.',
          'Em <b>Sistema → App da guarita</b>, informe a <b>versão publicada</b> — um número maior que o anterior.',
          'Escreva em <b>O que mudou</b> uma frase para o operador.',
          'Se a correção afeta dinheiro ou cancela, informe também a <b>versão mínima</b>: abaixo dela o aviso não pode ser dispensado.',
        ] },
        { tipo: 'aviso', tom: 'atencao', cobranca: true, titulo: 'Nessa ordem',
          texto:
            'Informando a versão antes de publicar o instalador, todos os aparelhos avisam de uma versão que ainda não ' +
            'dá para baixar. O app avisa; quem instala é o operador — ele não baixa nem instala sozinho.' },
        { tipo: 'subtitulo', texto: 'Buscar um parâmetro' },
        { tipo: 'p', texto:
          'No alto da tela há um campo de busca. Digite um pedaço do nome — <i>abandono</i>, <i>webhook</i>, ' +
          '<i>placa mínima</i> — e os resultados aparecem no lugar da seção, com o caminho até cada um. Clique (ou ' +
          '<b>Enter</b> para o primeiro) e a tela vai até o campo, abrindo o grupo se estiver recolhido, e o destaca. ' +
          'Não precisa de acento, e o nome técnico também serve.' },
        { tipo: 'atalhos', itens: [
          ['/', 'põe o cursor na busca'],
          ['Enter', 'abre o primeiro resultado'],
          ['Esc', 'limpa a busca e volta à seção'],
        ] },
        { tipo: 'subtitulo', texto: 'Como ler uma linha' },
        { tipo: 'p', texto:
          'Cada parâmetro mostra o nome, a primeira frase da explicação e, à direita, o valor. <b>saiba mais</b> abre a ' +
          'explicação inteira. <b>motor: X</b>, ao lado de alguns campos, é o valor que o motor está usando <i>agora</i>; ' +
          'em destaque, difere do que está gravado.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'O selo "reinicia o motor": a alteração fica pendente',
          texto:
            'Os campos com esse selo <b>não gravam na hora</b>. A linha alterada fica destacada e aparece no pé da tela a ' +
            'barra <b>"alterações pendentes no motor"</b>, que acompanha você pelas seções. Ajuste tudo o que precisa e ' +
            'clique em <b>Revisar e aplicar</b>: um quadro mostra o valor gravado e o novo de cada parâmetro, e ' +
            '<b>Aplicar e reiniciar o motor</b> grava todos de uma vez, com <b>um reinício só</b> — cerca de um minuto sem ' +
            'leituras. <b>Descartar</b> desfaz. Se o servidor recusar um valor, nada muda. Prefira fora do pico.' },
        { tipo: 'aviso', tom: 'atencao', semCobranca: true, titulo: 'Tipo de Operação, Modo Paralelo e Câmera Única pedem confirmação',
          texto:
            'Eles mudam o comportamento da plataforma inteira, na hora. O quadro de confirmação diz <b>o que vai acontecer</b> ' +
            'antes de você decidir. O controle só muda depois do "sim", e o botão que nasce selecionado é <b>Cancelar</b>.' },
        { tipo: 'aviso', tom: 'atencao', cobranca: true, titulo: 'Tipo de Operação, Modo Paralelo e Câmera Única pedem confirmação',
          texto:
            'Eles mudam o comportamento da plataforma inteira, na hora. O quadro de confirmação diz <b>o que vai acontecer</b> — ' +
            'desligar o Modo Paralelo, por exemplo, é a virada: a saída passa a depender do pagamento no caixa. O controle só ' +
            'muda depois do "sim", e o botão que nasce selecionado é <b>Cancelar</b>.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Os demais campos gravam sozinhos quando você sai deles — e só se o valor mudou',
          texto:
            'Clicar num campo e sair sem alterar não grava nada. Apagar o conteúdo e sair devolve o valor gravado. ' +
            'O que você grava vale <b>na hora</b>: o serviço que lê as câmeras consulta as configurações a cada quadro.' },
        { tipo: 'aviso', tom: 'atencao', semCobranca: true, titulo: 'Largura mínima da placa: não suba para "melhorar o OCR"',
          texto:
            'O valor de <b>50 px</b> é uma rede de segurança contra o trânsito da rua, não um filtro de qualidade. ' +
            'A leitura descartada <b>não é registrada</b>: subir esse valor perde veículos de verdade. Placas pequenas ' +
            'que leem mal são tratadas pela consolidação por passagem, que espera um quadro melhor.' },
        { tipo: 'aviso', tom: 'atencao', cobranca: true, titulo: 'Largura mínima da placa: não suba para "melhorar o OCR"',
          texto:
            'O valor de <b>50 px</b> é uma rede de segurança contra o trânsito da rua, não um filtro de qualidade. ' +
            'A leitura descartada <b>não vira ticket</b>: subir esse valor perde veículos de verdade. Placas pequenas ' +
            'que leem mal são tratadas pela consolidação por passagem, que espera um quadro melhor.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'minha-conta',
      titulo: 'Minha conta — a sua senha',
      blocos: [
        { tipo: 'p', texto: 'Todo usuário, de qualquer perfil, tem o atalho <b>Minha conta</b> no rodapé do menu, acima de <b>Sair</b>. Ali aparecem o usuário, o e-mail, o perfil e o último acesso.' },
        { tipo: 'figura', src: 'minha-conta.png', legenda: 'Minha conta: os seus dados e a troca da senha, que marca cada regra conforme você digita.' },
        { tipo: 'passos', itens: [
            'Clique em <b>Minha conta</b>.',
            'Em <b>Alterar senha</b>, informe a senha atual, a nova e a confirmação.',
            'A tela marca cada regra conforme você digita: pelo menos 8 caracteres, com letras e números, diferente da atual e do seu nome de usuário.',
          ] },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Esqueceu a senha?', texto: 'Fale com o administrador: ele define uma nova na tela Usuários. A plataforma não envia e-mail. Depois de 5 tentativas com a senha atual errada, a troca fica bloqueada por 15 minutos.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'seguranca',
      titulo: 'Segurança do dia a dia',
      blocos: [
        { tipo: 'lista', cobranca: true, itens: [
          '<b>Cada pessoa, um usuário.</b> Conta compartilhada apaga a auditoria: quando algo precisa ser explicado, ninguém sabe quem fez.',
          '<b>Saia ao deixar o posto.</b> A sessão fica aberta, e no computador da guarita isso significa que o próximo usa o seu nome.',
          '<b>Justificativa é registro, não formalidade.</b> O que você escreve ao isentar ou estornar é o que vai explicar a operação meses depois.',
          '<b>Não anote senha em papel colado no monitor.</b> Se precisar de acesso, peça um usuário próprio ao gestor.',
        ] },
        { tipo: 'lista', semCobranca: true, itens: [
          '<b>Cada pessoa, um usuário.</b> Conta compartilhada apaga a auditoria: quando algo precisa ser explicado, ninguém sabe quem fez.',
          '<b>Saia ao deixar o posto.</b> A sessão fica aberta, e no computador da portaria isso significa que o próximo usa o seu nome.',
          '<b>Troque a senha inicial.</b> Em <b>Minha conta</b>, no pé do menu, você troca a sua senha quando quiser — e deve trocar a que recebeu do gestor.',
          '<b>Não anote senha em papel colado no monitor.</b> Se precisar de acesso, peça um usuário próprio ao gestor.',
        ] },
      ],
    },
  ],
};
